import animate from '@play-co/timestep-core/lib/animate';
import View from '@play-co/timestep-core/lib/ui/View';
import uiConfig from 'src/lib/ui/config';
import { StackView } from '@play-co/timestep-core/ui';
import StateObserver from 'src/StateObserver';
import { createPersistentEmitter } from 'src/lib/Emitter';
import { SceneID, setTransitionPhase } from 'src/state/ui';
import {
  animDuration,
  getApplication,
  getRootView,
  isSplashScreenVisible,
  waitForItPromise,
  withLoading,
} from 'src/lib/utils';
import { assertNever } from 'src/replicant/utils';
import LivingRoomScene from '../scenes/LivingRoomScene';
import DiningRoomScene from '../scenes/DiningRoomScene';
import ToiletRoomScene from '../scenes/ToiletRoomScene';
import BedroomScene from '../scenes/BedroomScene';
import BathroomScene from '../scenes/BathroomScene';
import FriendsLivingRoomScene from '../scenes/FriendsLivingRoomScene';
import { FloatAnimator } from 'src/lib/Animator';
import DebutScene from '../scenes/DebutScene';
import { exhaustiveTuple } from 'src/replicant/utils/types';
import { Feature } from 'src/replicant/analytics';
import ArenaScene from '../scenes/ArenaScene';

export type Transition = 'fade' | 'slide_forward' | 'slide_back';

export interface TransitioningScene {
  prepareScene(): void;
  showScene(root: View): void;
  hideScene(root: View): void;

  transition(): Transition;
}

export function isTransitioningScene(scene: {}): scene is TransitioningScene {
  if (!scene) {
    return false;
  }

  const transitioningScene = scene as TransitioningScene;
  return (
    typeof transitioningScene.showScene === 'function' &&
    typeof transitioningScene.hideScene === 'function' &&
    typeof transitioningScene.transition === 'function' &&
    typeof transitioningScene.prepareScene === 'function'
  );
}

export const SLIDE_TRANSITION_DURATION = () => animDuration * 3;

type ValidSceneID = Exclude<SceneID, ''>;

/**
 * Scenes that serve as base scenes, where the pet should return after.
 * If the entered scene is one of these, that will be set as the base scene.
 */
const baseSceneIds = [
  'livingRoom',
  'friendsLivingRoom',
  'arena',
] as const satisfies ValidSceneID[];

export type BaseSceneID = (typeof baseSceneIds)[number];

export default class SceneManager {
  private rootView: StackView;
  private previousSceneID: SceneID | null = null;
  private readonly scenes: {
    livingRoom?: LivingRoomScene;
    diningRoom?: DiningRoomScene;
    toiletRoom?: ToiletRoomScene;
    bedroom?: BedroomScene;
    friendsLivingRoom?: FriendsLivingRoomScene;
    bathroom?: BathroomScene;
    debut?: DebutScene;
    arena?: ArenaScene;
  };
  public readonly loadingPriority = exhaustiveTuple<ValidSceneID>()(
    'livingRoom',
    'diningRoom',
    'bathroom',
    'toiletRoom',
    'bedroom',
    'friendsLivingRoom',
    'debut',
    'arena',
  );
  private loadingDynamicScenes = false;
  public get currentScene() {
    return this.scenes[this._currentScene as keyof typeof this.scenes];
  }
  private _currentScene: SceneID = '';

  /** Scene that the pet should go back after care action */
  private _baseSceneId: BaseSceneID = 'livingRoom';
  public get baseSceneId() {
    return this._baseSceneId;
  }

  constructor(opts: { rootView: StackView }) {
    this.rootView = opts.rootView;

    // Create scenes.
    this.scenes = {};

    // Listen to scene change.
    createPersistentEmitter(({ ui }) => ui.transition.nextScene).addListener(
      (name) => this.gotoScene(name),
    );
  }

  /**
   * Scene Navigation
   */

  private async gotoScene(name: SceneID) {
    // part of scene reload: don't do anything
    if (!name) return;

    if (!this.scenes[name]) {
      this.createScene(name);
    }

    const rootView = this.rootView;

    const scene = this.scenes[name];

    if (!scene) {
      throw new Error(`Scene ${name} not found`);
    }

    // If this scene can be a base scene, set it as such.
    if (baseSceneIds.includes(name as BaseSceneID)) {
      this._baseSceneId = name as BaseSceneID;
    }
    this._currentScene = name;

    const sceneView = scene.getView();
    const fromScene = rootView.stack[rootView.stack.length - 1];

    // Detect and remember previous scene
    const previousScene = this.previousSceneID
      ? this.scenes[this.previousSceneID]
      : null;
    this.previousSceneID = name;

    // Call hide callback on previous scene
    previousScene?.hideScene();

    // swipe transition type:
    const transition = previousScene ? scene.transition() : 'fade';

    // Allow scene to do some stuff before transition.
    scene.onBeforeTransition();

    // Load mandatory assets.
    await withLoading(() => scene.preload(), {
      feature: Feature.SCENE_MANAGER._,
      subFeature: Feature.SCENE_MANAGER.PRELOAD,
    });

    // Another scene was selected while loading, abort.
    if (name !== StateObserver.getState().ui.transition.nextScene) return;

    // Prepare scene, to be shown.
    scene.prepareScene();

    // Select scene transition animation
    switch (transition) {
      case 'fade':
        this.fadeAnimation(fromScene, sceneView, scene);
        break;
      case 'slide_back':
      case 'slide_forward':
        this.slideAnimation(fromScene, sceneView, scene);
        break;
      default:
        assertNever(transition);
    }
  }

  private fadeAnimation(
    fromScene: View,
    sceneView: View,
    scene: TransitioningScene,
  ) {
    // Under the splash the fade cannot be seen, and the launch sequence waits
    // for 'entered' before it dismisses the splash, so enter instantly.
    const instant = isSplashScreenVisible();

    // transition out
    this.fadeOut(fromScene, instant, () => {
      // this has become a computed based state, checkable with isSceneLeft()
      // StateObserver.dispatch(setTransitionPhase('left'));

      // update stack
      const rootView = this.rootView;
      rootView.pop(true);
      rootView.push(sceneView, true);

      // The new sceneView intial setup must happen BEFORE awaiting for loading
      // or otherwise we'll be seeing the screen while loading,
      // in other words, flickering.
      sceneView.updateOpts({
        visible: false,
        opacity: 0,
      });

      // initialize new scene (before new scene has faded in)
      StateObserver.dispatch(setTransitionPhase('entering'));
      this.fadeIn(sceneView, instant, () => {
        scene.showScene(rootView);
        StateObserver.dispatch(setTransitionPhase('entered'));
      });
    });
  }

  private slideAnimation(
    fromScene: View,
    targetScene: View,
    scene: TransitioningScene,
  ) {
    // Start loading items before show up
    scene.prepareScene();

    // initialize new scene (before new scene has faded in)
    StateObserver.dispatch(setTransitionPhase('entering'));

    targetScene.updateOpts({ x: uiConfig.width });

    const dir = scene.transition() === 'slide_back' ? -1 : 1;

    // Block the screen to avoid any interaction while the animation is being executed
    const blocker = new View({
      superview: getApplication().getRootView(),
      infinite: true,
      canHandleEvents: true,
      zIndex: 10000, // Under popups.
    });

    // Animate
    this.slideOut(fromScene, SLIDE_TRANSITION_DURATION(), -dir);

    targetScene.updateOpts({ zIndex: -999 });

    this.rootView.addSubview(targetScene);

    this.slideIn(targetScene, SLIDE_TRANSITION_DURATION(), dir);

    waitForItPromise(SLIDE_TRANSITION_DURATION()).then(() => {
      this.rootView.pop(true);

      const { width, height } = targetScene.style;
      this.rootView.push(targetScene, true);
      // Reset scene dimensions because StackView resizes pushed views to match itself
      targetScene.updateOpts({ width, height });

      scene.showScene(this.rootView);
      StateObserver.dispatch(setTransitionPhase('entered'));
      blocker.removeFromSuperview();
    });
  }

  private createScene(name: SceneID) {
    switch (name) {
      case 'livingRoom': {
        this.scenes.livingRoom = new LivingRoomScene();
        break;
      }

      case 'friendsLivingRoom': {
        this.scenes.friendsLivingRoom = new FriendsLivingRoomScene();
        break;
      }

      case 'diningRoom': {
        this.scenes.diningRoom = new DiningRoomScene();
        break;
      }

      case 'toiletRoom': {
        this.scenes.toiletRoom = new ToiletRoomScene();
        break;
      }

      case 'bedroom': {
        this.scenes.bedroom = new BedroomScene();
        break;
      }

      case 'bathroom': {
        this.scenes.bathroom = new BathroomScene();
        break;
      }

      case 'debut': {
        this.scenes.debut = new DebutScene();
        break;
      }

      case 'arena': {
        this.scenes.arena = new ArenaScene();
        break;
      }

      case '': {
        // The empty scene is a placeholder for animation purposes.
        // It should not need to be created.
        throw new Error('Trying to go to empty scene.');
      }

      default: {
        throw assertNever(name);
      }
    }
  }

  /**
  /* Scene transition animations
  */
  private slideOut(scene: View, duration: number, dir: number) {
    if (!scene) {
      return;
    }

    scene.show();
    const target = uiConfig.width * dir;

    animate(scene).clear().then({ x: target }, duration, animate.easeOut);
  }

  private slideIn(scene: View, duration: number, dir: number) {
    if (!scene) {
      return;
    }

    const from = uiConfig.width * dir;

    scene.updateOpts({ x: from });
    scene.show();

    animate(scene).clear().then({ x: 0 }, duration, animate.easeOut);
  }

  /**
  /* Scene transition animations
  */
  private fadeIn(scene: View, instant: boolean, cb: () => void) {
    if (!scene) {
      return;
    }

    scene.show();

    if (instant) {
      animate(scene).clear();
      scene.updateOpts({ opacity: 1, x: 0 });
      cb();
      return;
    }

    // The delay lets the scene settle its first layout before it is seen.
    animate(scene)
      .clear()
      .wait(animDuration)
      .then({ opacity: 1, x: 0 }, animDuration, animate.easeInOut)
      .then(cb);
  }

  private fadeOut(scene: View, instant: boolean, cb: () => void) {
    if (!scene) {
      cb();
      return;
    }

    if (instant) {
      animate(scene).clear();
      scene.hide();
      cb();
      return;
    }

    animate(scene)
      .clear()
      .then({ opacity: 0, x: 0 }, animDuration, animate.easeInOut)
      .then(() => {
        scene.hide();
        cb();
      });
  }

  /**
   * Show white screen animation
   */
  public async showWhiteScreenEffect(duration = 3000) {
    const { screenSize } = StateObserver.getState().ui;

    const background = new View({
      tag: 'glowBackground',
      backgroundColor: 'white',
      width: screenSize.width,
      height: screenSize.height,
      x: screenSize.left,
      y: screenSize.top,
      zIndex: 999,
    });

    const animator = new FloatAnimator((value) => {
      background.style.opacity = value;
    });

    getRootView().addSubview(background);
    await animator.setTarget(1, duration);

    return async () => {
      await animator.setTarget(0, duration);
      background.removeFromSuperview();
    };
  }

  /**
   * Preload all dynamic scenes in the background.
   * @returns
   */
  public async preloadScenes() {
    // Prevent multiple calls to this function.
    if (this.loadingDynamicScenes) return;
    this.loadingDynamicScenes = true;

    // Load all scenes in the loading priority order.
    for (const sceneId of this.loadingPriority) {
      await this.preloadScene(sceneId);
    }
  }

  /**
   * Preload a scene.
   * @param sceneId
   * @returns
   */
  public async preloadScene(sceneId: keyof SceneManager['scenes']) {
    if (!this.scenes[sceneId]) this.createScene(sceneId);
    await this.scenes[sceneId]?.preload();
  }

  /**
   * Get a scene by its ID.
   */
  public getScene<T extends keyof SceneManager['scenes']>(
    sceneId: T,
  ): SceneManager['scenes'][T] {
    if (!this.scenes[sceneId]) this.createScene(sceneId);
    return this.scenes[sceneId];
  }

  /**
   * Check if a scene is valid.
   */
  public isValidScene(
    sceneId: SceneID | undefined,
  ): sceneId is keyof SceneManager['scenes'] {
    return this.loadingPriority.includes(
      sceneId as keyof SceneManager['scenes'],
    );
  }

  /**
   * Pause current scene.
   */
  public pause() {
    this.currentScene?.pauseScene();
  }

  /**
   * Resume current scene.
   */
  public resume() {
    this.currentScene?.resumeScene();
  }
}
